import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Lock from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { validateResetToken, resetPassword } from 'Shared/Security/Domain/Services/PasswordResetService';
import { LOGIN } from 'Shared/Routing/Application/Constants/PathsConstant';

export default function ResetPasswordView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [checkingToken, setCheckingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState(null);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    async function checkToken() {
      if (!token) {
        setTokenValid(false);
        setTokenError(t('security.resetPassword.invalidToken'));
        setCheckingToken(false);
        return;
      }

      try {
        const res = await validateResetToken(token);
        if (res.valid) {
          setTokenValid(true);
        } else {
          setTokenValid(false);
          setTokenError(res.message || t('security.resetPassword.invalidToken'));
        }
      } catch (err) {
        setTokenValid(false);
        setTokenError(err.response?.data?.message || t('security.resetPassword.invalidToken'));
      } finally {
        setCheckingToken(false);
      }
    }

    checkToken();
  }, [token, t]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError(null);

    if (password.length < 8) {
      setSubmitError(t('security.resetPassword.tooShort'));
      return;
    }

    if (password !== confirmPassword) {
      setSubmitError(t('security.resetPassword.mismatch'));
      return;
    }

    setSubmitting(true);

    try {
      await resetPassword(token, password);
      setSubmitSuccess(true);
      setTimeout(() => {
        navigate(LOGIN);
      }, 3500);
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0] || t('security.resetPassword.error');
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `radial-gradient(1200px 600px at -10% -20%, rgba(25,118,210,0.15), transparent 60%),
                     radial-gradient(1000px 500px at 110% 120%, rgba(0,172,193,0.12), transparent 60%),
                     linear-gradient(135deg, #f6f9fc 0%, #eef5ff 100%)`,
        px: 2,
        py: { xs: 4, md: 6 }
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={8} sx={{ overflow: 'hidden', borderRadius: 3, p: { xs: 3, sm: 5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <img src="/img/logo.png" alt="logo" style={{ maxWidth: 160 }} />
          </Box>

          <Typography variant="h5" fontWeight={700} textAlign="center" sx={{ mb: 1 }}>
            {t('security.resetPassword.title')}
          </Typography>

          {checkingToken ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
              <CircularProgress size={40} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Verificando enlace...
              </Typography>
            </Box>
          ) : !tokenValid ? (
            <Box sx={{ mt: 2 }}>
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {tokenError || t('security.resetPassword.invalidToken')}
              </Alert>
              <Button
                fullWidth
                variant="contained"
                onClick={() => navigate(LOGIN)}
                sx={{ py: 1.2, textTransform: 'none' }}
              >
                {t('security.resetPassword.backToLogin')}
              </Button>
            </Box>
          ) : submitSuccess ? (
            <Box sx={{ mt: 2 }}>
              <Alert
                icon={<CheckCircleOutline fontSize="inherit" />}
                severity="success"
                sx={{ mb: 3, borderRadius: 2 }}
              >
                {t('security.resetPassword.success')}
              </Alert>
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
                Serás redirigido al inicio de sesión en unos segundos...
              </Typography>
              <Button
                fullWidth
                variant="contained"
                onClick={() => navigate(LOGIN)}
                endIcon={<ArrowForward />}
                sx={{ py: 1.2, textTransform: 'none' }}
              >
                {t('security.resetPassword.backToLogin')}
              </Button>
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
                {t('security.resetPassword.subtitle')}
              </Typography>

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="newPassword"
                  label={t('security.resetPassword.newPassword')}
                  type={showPassword ? 'text' : 'password'}
                  id="newPassword"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword((v) => !v)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirmPassword"
                  label={t('security.resetPassword.confirmPassword')}
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={submitting}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={() => setShowConfirmPassword((v) => !v)}
                          edge="end"
                          size="small"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                {submitError && (
                  <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                    {submitError}
                  </Alert>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={submitting || !password || !confirmPassword}
                  sx={{ mt: 3, py: 1.2, textTransform: 'none' }}
                  endIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <ArrowForward />}
                >
                  {t('security.resetPassword.submit')}
                </Button>

                <Button
                  fullWidth
                  variant="text"
                  color="inherit"
                  onClick={() => navigate(LOGIN)}
                  disabled={submitting}
                  sx={{ mt: 1.5, textTransform: 'none' }}
                >
                  {t('security.resetPassword.backToLogin')}
                </Button>
              </Box>
            </>
          )}

          <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mt: 4 }}>
            © {new Date().getFullYear()} — Intranet
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}
